import { useEffect, useState, useRef } from 'react'
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { setBluetoothState } from '@modules/blockly/features/blocklySlice';

export default function useBluetooth({ serviceID, characteristicID, namePrefix }) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const characteristicState = useRef(null)
  const connected = useRef(false)
  const receivedMessage = useRef('')

  function clearMessage() {
    receivedMessage.current = '';
  }

  async function sendMessage(msg) {
    if (characteristicState.current && connected.current) {
      const message = new TextEncoder().encode(`${msg}\n`)
      try {
        characteristicState.current.writeValueWithoutResponse(message).catch((error) => {
          connected.current = false;
          console.error('Error writing characteristic:', error);
        });
      } catch (error) {
        connected.current = false;
        console.error('Error sending message:', error);
      }
    } else {
      connected.current = false;
      console.log('Not connected!')
    }
  }

  function handleNotifications(event) {
    const value = event.target.value;
    const decoder = new TextDecoder('utf-8');
    const message = decoder.decode(value);
    receivedMessage.current = message.replace(/[^\x20-\x7E]/g, '').trim();
    // console.log('Raw-message:', message, receivedMessage.current);
  }

  async function startNotifications() {
    if (characteristicState.current) {
      try {
        await characteristicState.current.startNotifications();
        characteristicState.current.addEventListener('characteristicvaluechanged', handleNotifications);
        console.log('Notifications started');
      } catch (error) {
        console.error('Error starting notifications:', error);
      }
    }
  }

  async function stopNotifications() {
    if (characteristicState.current) {
      try {
        await characteristicState.current.stopNotifications();
        characteristicState.current.removeEventListener('characteristicvaluechanged', handleNotifications);
        console.log('Notifications stopped');
      } catch (error) {
        console.error('Error stopping notifications:', error);
      }
    }
  }

  async function disconnect() {
    if (characteristicState.current && characteristicState.current.service && characteristicState.current.service.device) {
      await stopNotifications(); // Stop notifications before disconnect
      characteristicState.current.service.device.gatt.disconnect();
    }
    connected.current = false;
    dispatch(setBluetoothState(false, null));
  }

  async function requestConnection() {
    try {
      const filters = []
      if (namePrefix) filters.push({ namePrefix })
      if (serviceID) filters.push({ services: [serviceID] })
      const foundDevice = await window.navigator.bluetooth.requestDevice({
        filters,
      })
      await foundDevice.gatt.connect()
      const service = await foundDevice.gatt.getPrimaryService(serviceID)
      const characteristic = await service.getCharacteristic(characteristicID)
      characteristicState.current = characteristic

      try {
        await characteristic.startNotifications()
        characteristic.addEventListener('characteristicvaluechanged', handleNotifications)
        console.log('Notifications started for incoming messages')
      } catch (error) {
        console.error('Error starting notifications:', error)
      }

      connected.current = !!characteristic
      dispatch(setBluetoothState(true, null));
    } catch (error) {
      dispatch(setBluetoothState(false, error.message));
    }
  }

  return {
    requestConnection,
    disconnect,
    startNotifications,
    sendMessage,
    connectedRef: connected,
    receivedMessageRef: receivedMessage,
    clearMessage,
  }
}
